/**
 * §21.2 — the shared event protocol.
 *
 * The same event names, payloads, and sequencing serve Cloud Mode and Local
 * Event Mode (§19.4). Every event carries a protocol version, session
 * identifier, event type, sequence number, server timestamp, and payload, and
 * clients ignore stale sequence numbers (§21.2, P-047).
 *
 * Payload sizes are held to the §22.4 targets. In particular the public display
 * and player payloads carry photo *identifiers*, never image bytes (§22.3).
 */

import { z } from "zod";
import { ANSWER_METHODS, ANSWER_OPINIONS, GRADING_OUTCOMES, SESSION_STATES } from "@tq/domain";

export const PROTOCOL_VERSION = 1;

// ─── Shared fragments ────────────────────────────────────────────────────────

/** §22.3 — a player as it appears in any public payload. Photos travel by URL. */
export const publicPlayerSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  organization: z.string().nullable().default(null),
  /**
   * §22.6 — where the display image lives, never the bytes themselves.
   *
   * It carries a URL rather than the profile id it used to, because the image
   * no longer comes from this server. In Connected Mode it is an absolute URL
   * on the object store's CDN domain, which is the whole point: the phones
   * fetch avatars straight from the edge instead of from the instance running
   * the session. In venue mode it is a site-relative path back to the host,
   * because there is no CDN on a venue LAN. Both are just `img src` to the
   * client, which is why the client does not know which mode it is in.
   */
  photoUrl: z.string().nullable().default(null),
});

export const boardCellSchema = z.object({
  questionId: z.string(),
  cellOrder: z.number().int(),
  cellValue: z.number().int(),
  state: z.enum(["Available", "Selected", "Answering", "Completed", "Cancelled", "Restored"]),
});

export const boardCategorySchema = z.object({
  categoryId: z.string(),
  name: z.string(),
  displayOrder: z.number().int(),
  cells: z.array(boardCellSchema),
});

export const boardStateSchema = z.object({
  valuePrefix: z.string(),
  categories: z.array(boardCategorySchema),
  completedCount: z.number().int(),
  totalCount: z.number().int(),
});

export const questionLeaderRowSchema = z.object({
  rank: z.number().int(),
  player: publicPlayerSchema,
  points: z.number().int(),
  answerMethod: z.enum(ANSWER_METHODS),
});

export const overallRowSchema = z.object({
  rank: z.number().int(),
  previousRank: z.number().int().nullable(),
  player: publicPlayerSchema,
  totalScore: z.number().int(),
  previousTotalScore: z.number().int(),
});

/**
 * One unresolved answer in the facilitator's review queue.
 *
 * The facilitator reads an original spelling rather than the normalized key,
 * and rules against the key — so one ruling covers every spelling that
 * collapsed into it.
 */
export const reviewItemSchema = z.object({
  /** The Answer Map key a ruling is recorded against. */
  normalizedAnswer: z.string(),
  /** What the facilitator reads: an answer as somebody actually typed it. */
  displayAnswer: z.string(),
  /** How many players this one ruling settles. */
  playerCount: z.number().int(),
  opinion: z.enum(ANSWER_OPINIONS),
  confidence: z.number(),
  /** Why the engine could not decide, in a sentence a facilitator can act on. */
  reason: z.string().nullable(),
  matchedConcepts: z.array(z.string()),
  missingConcepts: z.array(z.string()),
});

/** §12.3 — aggregate counts only. Never names of incorrect players, never raw text. */
export const responseSummarySchema = z.object({
  correct: z.number().int(),
  partial: z.number().int(),
  incorrect: z.number().int(),
  noAnswer: z.number().int(),
  typed: z.number().int(),
  multipleChoice: z.number().int(),
  /** Optional per-option distribution for multiple-choice questions. */
  choiceDistribution: z
    .array(z.object({ choiceId: z.string(), count: z.number().int() }))
    .optional(),
});

// ─── Event payloads ──────────────────────────────────────────────────────────

export const eventPayloads = {
  "session.lobby.updated": z.object({
    players: z.array(publicPlayerSchema),
    connectedCount: z.number().int(),
    joinUrl: z.string(),
    sessionCode: z.string(),
  }),

  "session.started": z.object({
    board: boardStateSchema,
  }),

  "board.updated": z.object({
    board: boardStateSchema,
  }),

  /**
   * §8.3 — published when the facilitator selects a cell. The round is in
   * `Reading`; `opensAt` marks the *end* of the reading window, so every
   * surface can show `Get Ready` and then switch to the countdown together.
   *
   * Carries no correct-answer data of any kind (§5.1, F-034).
   */
  "question.opened": z.object({
    roundId: z.string(),
    questionId: z.string(),
    sequenceNumber: z.number().int(),
    categoryName: z.string(),
    cellValue: z.number().int(),
    valuePrefix: z.string(),
    prompt: z.string(),
    answerMode: z.enum(["Both", "TextOnly", "MultipleChoiceOnly"]),
    /** Epoch ms. Selection time — when the prompt appeared and reading began. */
    readingStartedAt: z.number().int(),
    /** Epoch ms. End of the reading window and start of the scored countdown. */
    opensAt: z.number().int(),
    /** Epoch ms. Authoritative close time. Clients render countdowns locally (§21.7). */
    closesAt: z.number().int(),
    timeLimitSeconds: z.number().int(),
  }),

  /** §8.4 / F-037 — an extension updates the authoritative close time once. */
  "question.extended": z.object({
    roundId: z.string(),
    closesAt: z.number().int(),
    addedSeconds: z.number().int(),
  }),

  /** §22.4 — under 250 bytes. Count only, never the answers. */
  "answer.count.updated": z.object({
    roundId: z.string(),
    submitted: z.number().int(),
    eligible: z.number().int(),
    typed: z.number().int(),
    multipleChoice: z.number().int(),
  }),

  "question.closed": z.object({
    roundId: z.string(),
    closedAt: z.number().int(),
  }),

  /** §12.3 beat 1 — public question results. */
  "question.results.published": z.object({
    roundId: z.string(),
    correctAnswer: z.string(),
    explanation: z.string().nullable(),
    winner: z
      .object({
        player: publicPlayerSchema,
        points: z.number().int(),
        answerMethod: z.enum(ANSWER_METHODS),
        responseTimeMs: z.number().int(),
      })
      .nullable(),
    leaderboard: z.array(questionLeaderRowSchema),
    summary: responseSummarySchema,
    /** §12.5 — how long this beat runs before the standings beat. */
    beatDurationMs: z.number().int(),
    /**
     * §12.5 — this beat ends on a facilitator click, not on its duration.
     *
     * Carried on the event rather than read from session settings by each
     * client, because it is a fact about *this* beat: a facilitator flipping
     * the setting between the close and the results must not change how a beat
     * that is already on the projector behaves.
     */
    manualAdvance: z.boolean().default(false),
  }),

  /** §12.3 beat 2 — overall standings, animated from previous rank and score. */
  "standings.published": z.object({
    rows: z.array(overallRowSchema),
    beatDurationMs: z.number().int(),
    isFinal: z.boolean(),
    /** §12.5 — this beat ends on a facilitator click, not on its duration. */
    manualAdvance: z.boolean().default(false),
  }),

  /** §12.4 — private, on the player's own channel. */
  "player.result.published": z.object({
    roundId: z.string(),
    ownAnswer: z.string().nullable(),
    correctAnswer: z.string(),
    explanation: z.string().nullable(),
    outcome: z.enum(GRADING_OUTCOMES),
    answerMethod: z.enum(ANSWER_METHODS),
    points: z.number().int(),
    questionRank: z.number().int().nullable(),
    questionParticipants: z.number().int(),
    totalScore: z.number().int(),
    overallRank: z.number().int().nullable(),
    overallParticipants: z.number().int(),
    /** §12.4 — positive is upward. */
    rankDelta: z.number().int().nullable(),
    pointsToNextRank: z.number().int().nullable(),
  }),

  /** §5.4 — the standing shown on every screen, refreshed after each scored question. */
  "player.standing.updated": z.object({
    totalScore: z.number().int(),
    position: z.number().int().nullable(),
    participants: z.number().int(),
    pointsToNextRank: z.number().int().nullable(),
    rankDelta: z.number().int().nullable(),
  }),

  /** §26.1 — everything a reconnecting player needs to resume. */
  "player.state.restored": z.object({
    sessionState: z.enum(SESSION_STATES),
    totalScore: z.number().int(),
    position: z.number().int().nullable(),
    participants: z.number().int(),
    hasSubmittedCurrentRound: z.boolean(),
    currentRound: z.unknown().nullable(),
  }),

  "answer.accepted": z.object({
    roundId: z.string(),
    receivedAt: z.number().int(),
  }),

  "session.paused": z.object({ pausedAt: z.number().int() }),
  "session.resumed": z.object({ resumedAt: z.number().int() }),

  "session.completed": z.object({
    finalStandings: z.array(overallRowSchema),
    completedAt: z.number().int(),
  }),

  /** F-041 — a cancelled round affects no score and returns the cell. */
  "question.cancelled": z.object({
    roundId: z.string(),
    board: boardStateSchema,
  }),

  /**
   * F-073 — the low-bandwidth setting changed mid-session.
   *
   * A dedicated event rather than asking clients to refetch their snapshot:
   * sixty simultaneous snapshot fetches is the exact burst this setting exists
   * to avoid, and it would land on a network the facilitator has just told us
   * is struggling.
   */
  "session.settings.updated": z.object({
    lowBandwidthMode: z.boolean(),
    /**
     * §12.5 — whether the beats wait for the facilitator.
     *
     * Defaulted rather than required so a client that is newer than the server
     * it is talking to still accepts the event. A dropped settings event would
     * cost the room the low-bandwidth switch too, which is the more expensive
     * of the two to lose.
     */
    manualBeatAdvance: z.boolean().default(false),
    /**
     * §5.1 — whether the projector may make any sound.
     *
     * Defaulted to true for the same reason `manualBeatAdvance` is defaulted at
     * all: an older server that does not send the field must not be read as a
     * server asking for silence. The failure modes are not symmetric — a room
     * that unexpectedly has sound is a surprise, a room that has silently lost
     * it is a broken show — so the default is the louder one.
     */
    soundEnabled: z.boolean().default(true),
  }),

  /**
   * Answers the evaluator could not classify, waiting on a human.
   *
   * Facilitator channel only. Raw player text never reaches a public surface,
   * and the payload carries no player identity — only how many are affected,
   * which is what the facilitator needs in order to decide what to rule first.
   */
  "answers.review.required": z.object({
    roundId: z.string(),
    items: z.array(reviewItemSchema),
    /** Distinct players still without a finalized opinion. */
    playersAffected: z.number().int(),
  }),

  /** One ruling landed; the queue shrank by everyone who gave that answer. */
  "answers.review.resolved": z.object({
    roundId: z.string(),
    normalizedAnswer: z.string(),
    opinion: z.enum(ANSWER_OPINIONS),
    playersResolved: z.number().int(),
    remaining: z.array(reviewItemSchema),
  }),

  /** F-052 / P-048 — a manual correction is a visible, explained score change. */
  "score.corrected": z.object({
    sessionPlayerId: z.string(),
    delta: z.number().int(),
    reason: z.string(),
    totalScore: z.number().int(),
  }),

  /**
   * The facilitator is walking the room through a sample question.
   *
   * This is a TUTORIAL, not a round. No round exists, no cell is spent, nothing
   * is graded and no score event can result from it — the payload is a step
   * number, and every surface renders that step from copy it already has. That
   * is the whole mechanism, and it is why the walkthrough cannot affect a game
   * even if it is opened in the middle of one.
   *
   * `step` is null when the walkthrough closes and the projector should return
   * to whatever it was showing. It carries no copy and no step count — every
   * surface already holds the script, and putting the prose on the wire would
   * mean broadcasting a paragraph to the room on every click.
   *
   * Player devices ignore this event. The walkthrough is read off the projector
   * by a room that is looking up, and pushing it onto sixty phones as well would
   * put the explanation of where to look on the thing it is telling them to look
   * away from.
   */
  "sample.question.step": z.object({
    /** Zero-based beat, or null to end the walkthrough. */
    step: z.number().int().nullable(),
  }),
} as const;

export type EventType = keyof typeof eventPayloads;
export const EVENT_TYPES = Object.keys(eventPayloads) as EventType[];

export type EventPayload<T extends EventType> = z.infer<(typeof eventPayloads)[T]>;

/**
 * §21.4 — the payload size targets, as bytes of serialized JSON.
 *
 * One table, consumed by both the CI budget test and the facilitator health
 * panel, so a regression and the number shown in the room cannot disagree.
 * These bound the `payload` alone; `ENVELOPE_ALLOWANCE_BYTES` covers the
 * wrapper every event also pays for, which §21.4 does not count but the venue
 * Wi-Fi certainly does.
 */
export const PAYLOAD_BUDGET_BYTES = {
  "question.opened": 2048,
  "answer.count.updated": 250,
  "player.result.published": 2048,
  "question.results.published": 6144,
  "board.updated": 10240,
  "session.started": 10240,
  // Facilitator-only and only ever sent between close and results, so it is not
  // competing with a broadcast to the room. Sized for a long tail of one-player
  // answers rather than for the common handful.
  "answers.review.required": 8192,
  // Two integers. Budgeted anyway because it is broadcast to the room, and a
  // future temptation to put the step's prose on the wire should have to argue
  // with a number first — the copy already lives on every client.
  "sample.question.step": 250,
} as const satisfies Partial<Record<EventType, number>>;

export type BudgetedEventType = keyof typeof PAYLOAD_BUDGET_BYTES;

/** Measured worst case for the envelope fields around a payload. */
export const ENVELOPE_ALLOWANCE_BYTES = 160;

/** Serialized byte length of a value, counting UTF-8 rather than UTF-16 units. */
export function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/** §21.2 — the envelope every event travels in. */
export interface EventEnvelope<T extends EventType = EventType> {
  protocolVersion: number;
  sessionId: string;
  type: T;
  sequenceNumber: number;
  serverTimestamp: number;
  payload: EventPayload<T>;
}

export const envelopeSchema = z.object({
  protocolVersion: z.number().int(),
  sessionId: z.string(),
  type: z.string(),
  sequenceNumber: z.number().int(),
  serverTimestamp: z.number().int(),
  payload: z.unknown(),
});

export function createEvent<T extends EventType>(
  type: T,
  sessionId: string,
  sequenceNumber: number,
  payload: EventPayload<T>,
  now: number = Date.now(),
): EventEnvelope<T> {
  return {
    protocolVersion: PROTOCOL_VERSION,
    sessionId,
    type,
    sequenceNumber,
    serverTimestamp: now,
    payload,
  };
}

export function parseEvent(input: unknown): EventEnvelope | null {
  const envelope = envelopeSchema.safeParse(input);
  if (!envelope.success) return null;

  const type = envelope.data.type as EventType;
  const schema = eventPayloads[type];
  if (!schema) return null;

  const payload = schema.safeParse(envelope.data.payload);
  if (!payload.success) return null;

  return {
    protocolVersion: envelope.data.protocolVersion,
    sessionId: envelope.data.sessionId,
    type,
    sequenceNumber: envelope.data.sequenceNumber,
    serverTimestamp: envelope.data.serverTimestamp,
    payload: payload.data as EventPayload<EventType>,
  };
}

/**
 * §21.2 / P-047 — clients ignore stale sequence numbers so a late or duplicated
 * event never makes a screen jump backward.
 */
export class SequenceGate {
  #highest = 0;

  accept(event: { sequenceNumber: number }): boolean {
    if (event.sequenceNumber <= this.#highest) return false;
    this.#highest = event.sequenceNumber;
    return true;
  }

  get highest(): number {
    return this.#highest;
  }

  reset(): void {
    this.#highest = 0;
  }
}
